import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Replaces mobile's `notifications.store.ts` — that slice was an FCM dedup
 * cache shaped around remote-message types we don't receive in Telegram.
 * In the mini app we only need per-room unread counters maintained by the
 * WebSocket layer and bumped/cleared by chat screens.
 */
export type UnreadKind = 'team' | 'match' | 'tournament';

export interface UnreadState {
  /** counters per chat room id */
  rooms: Record<string, number>;
  /** aggregate badges shown in the TabBar */
  totals: Record<UnreadKind, number>;
}

const initialState: UnreadState = {
  rooms: {},
  totals: { team: 0, match: 0, tournament: 0 },
};

const unreadSlice = createSlice({
  name: 'unread',
  initialState,
  reducers: {
    bumpRoom: (state, action: PayloadAction<{ roomId: string; kind: UnreadKind }>) => {
      const { roomId, kind } = action.payload;
      state.rooms[roomId] = (state.rooms[roomId] ?? 0) + 1;
      state.totals[kind] += 1;
    },
    clearRoom: (state, action: PayloadAction<{ roomId: string; kind: UnreadKind }>) => {
      const { roomId, kind } = action.payload;
      const previous = state.rooms[roomId] ?? 0;
      delete state.rooms[roomId];
      state.totals[kind] = Math.max(0, state.totals[kind] - previous);
    },
    setKindTotal: (state, action: PayloadAction<{ kind: UnreadKind; total: number }>) => {
      state.totals[action.payload.kind] = action.payload.total;
    },
    reset: () => initialState,
  },
});

export const { bumpRoom, clearRoom, setKindTotal, reset: resetUnread } = unreadSlice.actions;
export default unreadSlice.reducer;
